import type { ReactChild, ReactElement } from 'react';
import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { uniqueId } from 'lodash';
import { FormGroup, Label } from 'reactstrap';
import type {
  GroupBase,
  OptionProps,
  OptionsOrGroups,
  Props as SelectProps
} from 'atom-components/select/select';
import Select, { components } from 'atom-components/select/select';
import './index.scss';

export type Props<
  SelectOption,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<SelectOption> = GroupBase<SelectOption>
> = {
  label: ReactChild;
  filteredIds?: (number | string)[]; // used to apply 'in-result-option' css class to options in the select menu
  options: OptionsOrGroups<SelectOption, Group> | undefined;
} & SelectProps<SelectOption, IsMulti, Group>;

/**
 * This component is a wrapper around [react-select](https://react-select.com) for use in report filters
 *
 *  - has all [react-select](https://react-select.com/props) props
 *  - requires a label, this will be linked to the input in an accessible manner
 */
export default function BaseFilter<
  Option,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  label,
  filteredIds,
  hideSelectedOptions = false,
  getOptionValue = (o): string => (o as unknown as { value }).value,
  ...selectProps
}: Props<Option, IsMulti, Group>): ReactElement {
  const [id] = useState(() => uniqueId('filter-'));

  const customOption = useCallback(
    (optionProps: OptionProps<Option, IsMulti>) => (
      <components.Option
        {...optionProps}
        className={clsx(
          optionProps.className,
          filteredIds?.includes(+getOptionValue(optionProps.data)) ? 'in-result-option' : ''
        )}
      />
    ),
    [filteredIds, getOptionValue]
  );
  return (
    <FormGroup className="flex-grow-1 flex-xl-grow-0 mb-3">
      <Label className="d-flex flex-column align-items-stretch fw-bold" htmlFor={id}>
        {label}
      </Label>
      <Select<Option, IsMulti, Group>
        placeholder="All"
        hideSelectedOptions={hideSelectedOptions}
        inputId={id}
        className={clsx('base-filter', { disabled: selectProps.isDisabled })}
        {...selectProps}
        getOptionValue={getOptionValue}
        components={{ Option: customOption, ...selectProps.components }}
      />
    </FormGroup>
  );
}
